import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Form,
  InputGroup,
  Card,
  Tabs,
  Tab,
  Pagination,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  getSubmissionListYear,
  getDistinctYears,
  getMissingSubmissions,
} from "../../../api/nebReportService";

function VerificationList() {
  const date = new Date();
  let currentMonth = date.getMonth();
  let currentYear = date.getFullYear();
  let currentQuarter = Math.ceil((currentMonth + 1) / 3) - 1;
  if (currentQuarter === 0) {
    currentQuarter = 4;
    currentYear -= 1;
  }

  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  const [selectedQuarter, setSelectedQuarter] = useState("Q" + currentQuarter);
  const [yearList, setYearList] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");

  // Submitted
  const [submittedData, setSubmittedData] = useState([]);
  const [filteredSubmitted, setFilteredSubmitted] = useState([]);
  const [loadingSubmitted, setLoadingSubmitted] = useState(true);

  // Not Submitted
  const [missingData, setMissingData] = useState([]);
  const [filteredMissing, setFilteredMissing] = useState([]);
  const [loadingMissing, setLoadingMissing] = useState(true);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  const fetchSubmitted = async (year, quarter = "") => {
    setLoadingSubmitted(true);
    try {
      const response = await getSubmissionListYear(year, quarter);
      let result = response.data || [];

      const filteredResult = result.filter(
        (item) => item.reportStatus !== "Draft"
      );

      setSubmittedData(filteredResult);
      setFilteredSubmitted(filteredResult);
    } finally {
      setLoadingSubmitted(false);
    }
  };

  const fetchMissing = async (year, quarter = "", page = 1) => {
    setLoadingMissing(true);
    try {
      const response = await getMissingSubmissions(
        year,
        quarter,
        page - 1,
        itemsPerPage
      ); // page index starts from 0
      const result = response.data.content || [];
      setMissingData(result);
      setFilteredMissing(result);
      setTotalPages(response.data.totalPages);
    } finally {
      setLoadingMissing(false);
    }
  };

  useEffect(() => {
    fetchSubmitted(selectedYear, selectedQuarter);
    fetchMissing(selectedYear, selectedQuarter, currentPage);
  }, []);

  useEffect(() => {
    const fetchYears = async () => {
      const result = await getDistinctYears();
      setYearList(result.data.sort((a, b) => b - a));
    };
    fetchYears();
  }, []);

  useEffect(() => {
    const filteredSubmittedResult = submittedData.filter((item) =>
      item.agency?.agencyName
        ?.toLowerCase()
        .includes(searchKeyword.toLowerCase())
    );
    const filteredMissingResult = missingData.filter((item) =>
      item.agency?.agencyName
        ?.toLowerCase()
        .includes(searchKeyword.toLowerCase())
    );
    setFilteredSubmitted(filteredSubmittedResult);
    setFilteredMissing(filteredMissingResult);
  }, [searchKeyword, submittedData, missingData]);

  // Trigger fetchMissing on page/year/quarter change
  useEffect(() => {
    fetchMissing(selectedYear, selectedQuarter, currentPage);
  }, [currentPage, selectedYear, selectedQuarter]);

  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectedYear(year);
    setCurrentPage(1);
    fetchSubmitted(year, selectedQuarter);
  };

  const handleQuarterChange = (e) => {
    const quarter = e.target.value;
    setSelectedQuarter(quarter);
    setCurrentPage(1);
    fetchSubmitted(selectedYear, quarter);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const paginationItems = [];
    for (let i = 1; i <= totalPages; i++) {
      paginationItems.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="mt-3 justify-content-center">
        {paginationItems}
      </Pagination>
    );
  };

  return (
    <Card className="mt-4">
      <Card.Header>
        <InputGroup className="mb-2">
          <InputGroup.Text>Year / Quarter :</InputGroup.Text>
          <Form.Select
            className="col-sm-3 col-lg-2"
            value={selectedYear}
            onChange={handleYearChange}
          >
            {yearList.map((year, index) => (
              <option key={index} value={year}>
                {year}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            className="col-sm-3 col-lg-2"
            value={selectedQuarter}
            onChange={handleQuarterChange}
          >
            <option value="">All Quarters</option>
            <option value="Q1">Q1</option>
            <option value="Q2">Q2</option>
            <option value="Q3">Q3</option>
            <option value="Q4">Q4</option>
          </Form.Select>
        </InputGroup>

        <Form.Group>
          <Form.Control
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            placeholder="Search agency name..."
          />
        </Form.Group>
      </Card.Header>

      <Card.Body>
        <Tabs defaultActiveKey="submitted">
          <Tab eventKey="submitted" title="Submitted">
            {loadingSubmitted ? (
              <p>Loading...</p>
            ) : (
              <Table responsive bordered hover size="sm" className="mt-1">
                <thead>
                  <tr>
                    <th style={{ width: "3em" }}>No</th>
                    <th>Agency</th>
                    <th>Status</th>
                    <th>Approver</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubmitted.length === 0 ? (
                    <tr>
                      <td colSpan={5}>No records found</td>
                    </tr>
                  ) : (
                    filteredSubmitted.map((report, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>
                          {report.reportStatus === "Not Submit" ? (
                            <span
                              style={{
                                color: "#0d6efd",
                                cursor: "pointer",
                              }}
                            >
                              {report.agency.agencyName}
                            </span>
                          ) : (
                            <Link
                              to={`/neb-verification/neb-verification-view/pending/${report.id}/${report.agency.id}/${report.reportQuarter}/${report.reportPeriodYear}/${report.reportStatus}`}
                              className=""
                            >
                              {report.agency.agencyName}
                            </Link>
                          )}
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              report.reportStatus === "Approved"
                                ? "bg-success"
                                : report.reportStatus === "Pending for Approval"
                                ? "bg-warning"
                                : report.reportStatus ===
                                  "Pending for Amendment"
                                ? "bg-danger"
                                : "bg-secondary"
                            }`}
                          >
                            {report.reportStatus}
                          </span>
                        </td>
                        <td>
                          {report.reportStatus === "Approved" ||
                          report.reportStatus === "Pending for Amendment"
                            ? report.modifiedBy
                            : "-"}
                        </td>
                        <td>
                          <div>
                            {report.reportStatus === "Not Submit" ||
                            report.reportStatus === "Draft" ? (
                              <span>-</span>
                            ) : (
                              <Link
                                to={`/neb-verification/neb-verification-view/pending/${report.id}/${report.agency.id}/${report.reportQuarter}/${report.reportPeriodYear}/${report.reportStatus}`}
                                className=""
                              >
                                View
                              </Link>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </Tab>

          <Tab eventKey="notSubmitted" title="Not Submitted">
            {loadingMissing ? (
              <p>Loading...</p>
            ) : (
              <>
                <Table responsive bordered hover size="sm" className="mt-1">
                  <thead>
                    <tr>
                      <th style={{ width: "3em" }}>No</th>
                      <th>Agency</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMissing.length === 0 ? (
                      <tr>
                        <td colSpan={3}>No records found</td>
                      </tr>
                    ) : (
                      filteredMissing.map((item, i) => (
                        <tr key={i}>
                          <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                          <td>{item.agency?.agencyName}</td>
                          <td>
                            <span className="badge bg-secondary">
                              Not Submitted
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
                {renderPagination()}
              </>
            )}
          </Tab>
        </Tabs>
      </Card.Body>
    </Card>
  );
}

export default VerificationList;
